import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BoardLine, TickingNumber } from "./MarketBoard";

/*
 * TiltGlowCard — 3D tilt that follows the cursor, a faint grid texture, a large
 * floating icon with parallax depth, and a "living" board layer: a full-width
 * background sparkline that scrolls slowly + a ticking number.
 * Adapted to JS + react-router and reskinned to the Nexxis brand from a
 * 21st.dev product-highlight card.
 */
export function TiltGlowCard({
  icon: Icon,
  num,
  title,
  body,
  notes = [],
  to = "/contact",
  ctaLabel = "Partner with us",
  testId,
  className = "",
  // living board layer
  lineSeed = 0,
  value,
  metricLabel = "30D",
  variant = "sober",
}) {
  const reduced = useReducedMotion();
  const [hover, setHover] = useState(false);

  const vivid = variant === "vivid";
  const lineOpacity = vivid ? 0.18 : 0.09;
  const lineDuration = vivid ? 54 : 82;

  // normalized cursor position 0..1 (size-independent)
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);

  const rotateX = useSpring(useTransform(my, [0, 1], [8, -8]), { stiffness: 300, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-8, 8]), { stiffness: 300, damping: 20 });

  const onMove = (e) => {
    if (reduced) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => {
    setHover(false);
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <motion.div
      data-testid={testId}
      onMouseEnter={() => setHover(true)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={reduced ? undefined : { rotateX, rotateY, transformStyle: "preserve-3d" }}
      className={`relative h-full min-h-[380px] w-full rounded-[18px] p-3 shadow-lg transition-shadow duration-300 hover:shadow-2xl ${className}`}
    >
      <div
        style={{ transform: "translateZ(20px)", transformStyle: "preserve-3d" }}
        className="nxx-tiltcard-panel relative h-full overflow-hidden rounded-[14px]"
      >
        {/* base teal fill (theme-aware: stays a deep teal card in both modes) */}
        <div className="nxx-tiltcard-surface absolute inset-0" />

        {/* faint grid texture with radial mask */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#BEE5E012_1px,transparent_1px),linear-gradient(to_bottom,#BEE5E012_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_40%,#000_70%,transparent_100%)]" />

        {/* living background sparkline (scrolls slowly, perpetual) */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[60%]">
          <BoardLine seed={lineSeed} opacity={lineOpacity} duration={lineDuration} />
        </div>

        {/* large floating icon (parallax) */}
        {Icon ? (
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-6 -right-4"
            style={{ transform: "translateZ(55px)" }}
            animate={reduced ? {} : { scale: hover ? 1.08 : 1, x: hover ? -8 : 0, y: hover ? -10 : 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <Icon size={180} strokeWidth={0.9} style={{ color: "rgba(190,229,224,0.14)" }} />
          </motion.div>
        ) : null}

        {/* content */}
        <div className="relative z-10 flex h-full min-h-[356px] flex-col p-7">
          <div className="flex items-start justify-between">
            {Icon ? <Icon size={22} strokeWidth={1.4} style={{ color: "#BEE5E0" }} /> : <span />}

            {/* data corner: order no. + ticking metric */}
            <div className="flex flex-col items-end gap-2">
              {num ? (
                <span className="font-display leading-none" style={{ fontSize: 22, color: "rgba(190,229,224,0.85)" }}>
                  {num}<span style={{ color: "#FF6857" }}>.</span>
                </span>
              ) : null}
              {value != null ? (
                <div className="flex items-baseline gap-1.5">
                  <TickingNumber value={value} reduced={reduced} style={{ fontSize: 13, letterSpacing: "0.02em" }} />
                  <span
                    className="font-ui"
                    style={{ fontSize: 9, letterSpacing: "0.18em", color: "rgba(190,229,224,0.5)" }}
                  >
                    {metricLabel}
                  </span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-auto max-w-[82%]">
            <h2
              className="font-display"
              style={{ fontSize: 28, lineHeight: 1.08, color: "#F7F6F2", fontWeight: 500, letterSpacing: "-0.01em" }}
            >
              {title}
            </h2>
            <div className="mt-3 h-px" style={{ background: "#FF6857", width: 32 }} />
            <p className="font-ui mt-3" style={{ color: "rgba(247,246,242,0.78)", lineHeight: 1.55, fontSize: 13.5 }}>
              {body}
            </p>

            {notes.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-x-3 gap-y-1">
                {notes.map((n) => (
                  <span
                    key={n}
                    className="font-ui inline-flex items-center gap-1.5 text-[11px]"
                    style={{ color: "rgba(190,229,224,0.85)" }}
                  >
                    <span className="h-1 w-1 rounded-full" style={{ background: "#FF6857" }} />
                    {n}
                  </span>
                ))}
              </div>
            )}

            <Link
              to={to}
              className="mt-5 inline-flex items-center gap-2 font-ui text-[12px] font-semibold transition-opacity hover:opacity-80"
              style={{ color: "#BEE5E0", letterSpacing: "0.03em" }}
            >
              <span>{ctaLabel}</span>
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
